import { registerSW } from 'virtual:pwa-register';

/**
 * Injetado pelo Vite no build (ver `define` em packages/web/vite.shared.ts).
 * Declarado aqui dentro, e não num .d.ts do pacote: os tsconfig dos apps
 * só incluem a própria pasta `src`, então um ambiente global daqui nunca
 * seria carregado por eles.
 */
declare const __BUILD_ID__: string | undefined;

/**
 * Um retângulo cru, fora do React — o recarregamento que vem em seguida
 * apaga a árvore React inteira de qualquer forma, então não há por que
 * montar componente para algo que vive menos de um segundo.
 *
 * Cor em hexadecimal, não `var(--c-brand)`: isto roda antes de qualquer
 * garantia de que o CSS da marca terminou de carregar — se a checagem de
 * versão nova disparar cedo o bastante no boot, a variável ainda não
 * existe, a variável não resolvida vira transparente, e o retângulo
 * "carimba" preto puro por cima de tudo. Era exatamente essa tela preta
 * ao reabrir o app que a correção anterior não previu.
 */
function coverBeforeReload(): void {
  if (document.getElementById('mobi-reload-cover')) return;
  const cover = document.createElement('div');
  cover.id = 'mobi-reload-cover';
  cover.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;' +
    'background:linear-gradient(165deg,#ff8f3d,#ff5a1f 55%,#c2360a);';
  document.body.appendChild(cover);

  // Rede de segurança: se por qualquer motivo o recarregamento não
  // acontecer (updateSW falhou em silêncio, SW preso), a pessoa não pode
  // ficar presa numa tela preta para sempre — melhor mostrar o app com a
  // versão que já tinha do que uma cobertura que nunca sai da frente.
  setTimeout(() => cover.remove(), 4_000);
}

/**
 * Mantém o app atualizado.
 *
 * `autoUpdate` sozinho não resolve no iPhone: reabrir o PWA pelo
 * multitarefa restaura a página em vez de navegar, e o navegador nem
 * chega a perguntar se existe versão nova. O app fica preso numa versão
 * antiga indefinidamente — e nenhum deploy chega em quem já instalou.
 *
 * Aqui a checagem é explícita: toda vez que o app volta para a frente, e
 * a cada poucos minutos com ele aberto.
 */
export function setupAutoUpdate(): void {
  const updateSW = registerSW({
    immediate: true,

    onNeedRefresh() {
      // Cobre a tela ANTES de recarregar. A checagem roda em segundo
      // plano — inclusive no meio do uso, com o mapa aberto — e sem isto
      // a pessoa via a tela antiga por um instante até o recarregamento
      // assumir; parecia a tela principal piscando antes do splash, não
      // o splash aparecendo primeiro como devia.
      coverBeforeReload();
      void updateSW(true);
    },

    onRegisteredSW(_url, registration) {
      if (!registration) return;

      const check = () => {
        if (document.visibilityState === 'visible') void registration.update();
      };

      document.addEventListener('visibilitychange', check);
      window.addEventListener('focus', check);
      // Rede volta depois de um túnel: momento natural para conferir.
      window.addEventListener('online', check);

      const timer = setInterval(check, 5 * 60_000);
      window.addEventListener('pagehide', () => clearInterval(timer));
    },
  });
}

/** Identificador do build, para saber qual versão está no aparelho. */
export const BUILD_ID: string =
  typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
